import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from "react-country-flag";
import logo from "../traderhub24-logo.svg";
import Footer from "./components/Footer";
import Login from "./components/Login";
import Register from "./components/Register";
import marketHolidays from "../scripts/market-holidays.json";
import marketHours from "../scripts/market-hours.json";

function PublicTools({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation(['common', 'tools', 'pages']);
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // SEO: Update document title and meta tags
  useEffect(() => {
    document.title = 'Trading Tools - Risk Reward Calculator | TraderHub24';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Free trading tools including risk/reward calculator, position size calculator, win rate calculator, profit factor calculator, and real-time market hours tracker for global stock exchanges.');

    // Update or create Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
    if (!document.querySelector('meta[property="og:title"]')) {
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', 'Trading Tools - Risk Reward Calculator | TraderHub24');

    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
    if (!document.querySelector('meta[property="og:description"]')) {
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', 'Free trading tools including risk/reward calculator, position size calculator, win rate calculator, profit factor calculator, and real-time market hours tracker for global stock exchanges.');

    const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta');
    if (!document.querySelector('meta[property="og:url"]')) {
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', 'https://traderhub24.com/tools');

    const ogType = document.querySelector('meta[property="og:type"]') || document.createElement('meta');
    if (!document.querySelector('meta[property="og:type"]')) {
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', 'website');

    const ogImage = document.querySelector('meta[property="og:image"]') || document.createElement('meta');
    if (!document.querySelector('meta[property="og:image"]')) {
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', 'https://traderhub24.com/traderhub24-logo.svg');

    // Twitter Card tags
    const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta');
    if (!document.querySelector('meta[name="twitter:card"]')) {
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = document.querySelector('meta[name="twitter:title"]') || document.createElement('meta');
    if (!document.querySelector('meta[name="twitter:title"]')) {
      twitterTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twitterTitle);
    }
    twitterTitle.setAttribute('content', 'Trading Tools - Risk Reward Calculator | TraderHub24');

    const twitterDescription = document.querySelector('meta[name="twitter:description"]') || document.createElement('meta');
    if (!document.querySelector('meta[name="twitter:description"]')) {
      twitterDescription.setAttribute('name', 'twitter:description');
      document.head.appendChild(twitterDescription);
    }
    twitterDescription.setAttribute('content', 'Free trading tools including risk/reward calculator, position size calculator, win rate calculator, profit factor calculator, and real-time market hours tracker for global stock exchanges.');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://traderhub24.com/tools');

    // Structured Data (JSON-LD)
    const existingScript = document.getElementById('tools-structured-data');
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement('script');
    script.id = 'tools-structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Trading Tools - Risk Reward Calculator",
      "description": "Free trading tools including risk/reward calculator, position size calculator, win rate calculator, profit factor calculator, and real-time market hours tracker for global stock exchanges.",
      "url": "https://traderhub24.com/tools",
      "inLanguage": ["en", "tr", "sv"],
      "isPartOf": {
        "@type": "WebSite",
        "name": "TraderHub24",
        "url": "https://traderhub24.com"
      },
      "about": {
        "@type": "Thing",
        "name": "Trading Tools",
        "description": "Financial calculators and tools for traders"
      },
      "hasPart": [
        {
          "@type": "SoftwareApplication",
          "name": "Risk Reward Calculator",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        },
        {
          "@type": "SoftwareApplication",
          "name": "Win Rate Calculator",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        },
        {
          "@type": "SoftwareApplication",
          "name": "Profit Factor Calculator",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.getElementById('tools-structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Risk/Reward & Position Size State
  const [entryPrice, setEntryPrice] = useState("");
  const [stopLoss, setStopLoss] = useState("");
  const [target1, setTarget1] = useState("");
  const [target2, setTarget2] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [riskPercent, setRiskPercent] = useState("1");
  const [rrResult, setRrResult] = useState<{
    rr1: number | null;
    rr2: number | null;
    suggestedUnits: number | null;
    riskDollar: number | null;
    unitRiskDollar: number | null;
  }>({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });

  // Win Rate Calculator State
  const [winningTrades, setWinningTrades] = useState("");
  const [losingTrades, setLosingTrades] = useState("");
  const [winRate, setWinRate] = useState<number | null>(null);

  // Profit Factor Calculator State
  const [totalProfit, setTotalProfit] = useState("");
  const [totalLoss, setTotalLoss] = useState("");
  const [profitFactor, setProfitFactor] = useState<number | null>(null);

  // Market Hours Tools State
  interface MarketInfo {
    name: string;
    countryCode: string;
    isOpen: boolean;
    timeToOpen: string | null;
    timeToClose: string | null;
    openTimeLocal: string | null;
    closeTimeLocal: string | null;
  }

  const [marketsStatus, setMarketsStatus] = useState<MarketInfo[]>([]);
  const [currentUtcTime, setCurrentUtcTime] = useState<Date | null>(null);
  const [localTime, setLocalTime] = useState<string>("");

  // Reset functions
  const resetRiskRewardValues = () => {
    setEntryPrice("");
    setStopLoss("");
    setTarget1("");
    setTarget2("");
    setAccountSize("");
    setRiskPercent("1");
    setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
  };

  const resetWinRateValues = () => {
    setWinningTrades("");
    setLosingTrades("");
    setWinRate(null);
  };

  const resetProfitFactorValues = () => {
    setTotalProfit("");
    setTotalLoss("");
    setProfitFactor(null);
  };

  // Calculate Risk/Reward & Position Size automatically
  useEffect(() => {
    const entry = parseFloat(entryPrice);
    const stop = parseFloat(stopLoss);
    const tp1 = parseFloat(target1);
    const tp2 = parseFloat(target2);
    const account = parseFloat(accountSize);
    const risk = parseFloat(riskPercent);

    if (!entry || !stop || !account || !risk || risk <= 0 || account <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
      return;
    }

    if (entry <= 0 || stop <= 0) {
      setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
      return;
    }

    const isLong = entry > stop;
    let riskAmount: number;
    let reward1: number | null = null;
    let reward2: number | null = null;

    if (isLong) {
      if (stop >= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
        return;
      }
      riskAmount = entry - stop;
      if (tp1 && tp1 > entry) reward1 = tp1 - entry;
      if (tp2 && tp2 > entry) reward2 = tp2 - entry;
    } else {
      if (stop <= entry) {
        setRrResult({ rr1: null, rr2: null, suggestedUnits: null, riskDollar: null, unitRiskDollar: null });
        return;
      }
      riskAmount = stop - entry;
      if (tp1 && tp1 < entry) reward1 = entry - tp1;
      if (tp2 && tp2 < entry) reward2 = entry - tp2;
    }

    const riskDollar = (account * risk) / 100;
    const suggestedUnits = riskAmount > 0 ? riskDollar / riskAmount : null;
    const unitRiskDollar = riskAmount;
    const rr1 = reward1 !== null && riskAmount > 0 ? reward1 / riskAmount : null;
    const rr2 = reward2 !== null && riskAmount > 0 ? reward2 / riskAmount : null;

    setRrResult({ rr1, rr2, suggestedUnits, riskDollar, unitRiskDollar });
  }, [entryPrice, stopLoss, target1, target2, accountSize, riskPercent]);

  // Calculate Win Rate automatically
  useEffect(() => {
    const wins = parseFloat(winningTrades);
    const losses = parseFloat(losingTrades);

    if (wins >= 0 && losses >= 0 && (wins > 0 || losses > 0)) {
      const totalTrades = wins + losses;
      if (totalTrades > 0) {
        setWinRate((wins / totalTrades) * 100);
      } else {
        setWinRate(null);
      }
    } else {
      setWinRate(null);
    }
  }, [winningTrades, losingTrades]);

  // Calculate Profit Factor automatically
  useEffect(() => {
    const profit = parseFloat(totalProfit);
    const loss = parseFloat(totalLoss);

    if (profit >= 0 && loss > 0) {
      setProfitFactor(profit / loss);
    } else {
      setProfitFactor(null);
    }
  }, [totalProfit, totalLoss]);

  // Update UTC time
  useEffect(() => {
    const updateUtcTime = () => setCurrentUtcTime(new Date());
    updateUtcTime();
    const timeInterval = setInterval(updateUtcTime, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Update local time display
  useEffect(() => {
    const updateLocalTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');
      setLocalTime(`${hours}:${minutes}:${seconds}`);
    };
    updateLocalTime();
    const localTimeInterval = setInterval(updateLocalTime, 1000);
    return () => clearInterval(localTimeInterval);
  }, []);

  // Calculate Market Hours (simplified version - using the same logic as LandingPage)
  useEffect(() => {
    if (!currentUtcTime) return;

    const checkHoliday = (exchange: string): { status: string; open?: string; close?: string } | null => {
      const today = currentUtcTime;
      const todayStr = today.toISOString().split('T')[0];
      
      for (const key in marketHolidays) {
        const holiday = marketHolidays[key as keyof typeof marketHolidays];
        if (holiday.date === todayStr && holiday.exchange === exchange) {
          return {
            status: holiday.status,
            open: 'open' in holiday ? holiday.open : undefined,
            close: 'close' in holiday ? holiday.close : undefined
          };
        }
      }
      return null;
    };

    const calculateMarketStatus = (
      exchange: string,
      openHour: number,
      openMinute: number,
      closeHour: number,
      closeMinute: number,
      timezoneOffset: number
    ): { isOpen: boolean; timeToOpen: string | null; timeToClose: string | null; openTimeLocal: string | null; closeTimeLocal: string | null } => {
      const nowUtc = currentUtcTime.getTime();
      const marketLocalTime = new Date(nowUtc - (timezoneOffset * 3600000));
      const marketDay = marketLocalTime.getDay();
      const marketLocalHours = marketLocalTime.getHours();
      const marketMinutes = marketLocalTime.getMinutes();
      const marketCurrentTime = marketLocalHours * 60 + marketMinutes;
      const marketOpenTime = openHour * 60 + openMinute;

      const formatTimeLocal = (utcTimestamp: number): string => {
        const localDate = new Date(utcTimestamp);
        const hours = localDate.getHours().toString().padStart(2, '0');
        const minutes = localDate.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      };

      const getCloseTimeLocal = (marketDate: Date): string => {
        let utcCloseHour = closeHour - timezoneOffset;
        let utcCloseMinute = closeMinute;
        
        if (utcCloseHour >= 24) {
          utcCloseHour -= 24;
        } else if (utcCloseHour < 0) {
          utcCloseHour += 24;
        }
        
        let closeTimeUtc = new Date(marketDate);
        closeTimeUtc = new Date(closeTimeUtc.getTime() - (timezoneOffset * 3600000));
        closeTimeUtc.setUTCHours(utcCloseHour, utcCloseMinute, 0, 0);
        
        return formatTimeLocal(closeTimeUtc.getTime());
      };

      const getNextOpenUtc = (startDate: Date): number => {
        let utcOpenHour = openHour - timezoneOffset;
        let utcOpenMinute = openMinute;
        
        if (utcOpenHour >= 24) {
          utcOpenHour -= 24;
        } else if (utcOpenHour < 0) {
          utcOpenHour += 24;
        }
        
        let nextOpenUtc = new Date(startDate);
        nextOpenUtc = new Date(nextOpenUtc.getTime() - (timezoneOffset * 3600000));
        nextOpenUtc.setUTCHours(utcOpenHour, utcOpenMinute, 0, 0);
        
        if (marketCurrentTime >= marketOpenTime) {
          nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1);
        }
        
        let marketLocalDate = new Date(nextOpenUtc.getTime() + (timezoneOffset * 3600000));
        let marketDay = marketLocalDate.getDay();
        
        while (marketDay === 0 || marketDay === 6) {
          nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1);
          marketLocalDate = new Date(nextOpenUtc.getTime() + (timezoneOffset * 3600000));
          marketDay = marketLocalDate.getDay();
        }
        
        let nextDateStr = marketLocalDate.toISOString().split('T')[0];
        let nextHoliday = null;
        for (const key in marketHolidays) {
          const h = marketHolidays[key as keyof typeof marketHolidays];
          if (h.date === nextDateStr && h.exchange === exchange) {
            nextHoliday = h;
            break;
          }
        }
        
        while (nextHoliday && nextHoliday.status === "closed") {
          nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1);
          marketLocalDate = new Date(nextOpenUtc.getTime() + (timezoneOffset * 3600000));
          marketDay = marketLocalDate.getDay();
          while (marketDay === 0 || marketDay === 6) {
            nextOpenUtc.setUTCDate(nextOpenUtc.getUTCDate() + 1);
            marketLocalDate = new Date(nextOpenUtc.getTime() + (timezoneOffset * 3600000));
            marketDay = marketLocalDate.getDay();
          }
          nextDateStr = marketLocalDate.toISOString().split('T')[0];
          nextHoliday = null;
          for (const key in marketHolidays) {
            const h = marketHolidays[key as keyof typeof marketHolidays];
            if (h.date === nextDateStr && h.exchange === exchange) {
              nextHoliday = h;
              break;
            }
          }
        }
        
        return nextOpenUtc.getTime();
      };
      
      const holiday = checkHoliday(exchange);
      
      const isMarketOpenLocal = (openTimeLocalStr: string | null, closeTimeLocalStr: string | null): boolean => {
        if (!openTimeLocalStr || !closeTimeLocalStr) return false;
        
        const now = new Date();
        const [openHour, openMinute] = openTimeLocalStr.split(':').map(Number);
        const [closeHour, closeMinute] = closeTimeLocalStr.split(':').map(Number);
        
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const openTime = openHour * 60 + openMinute;
        const closeTime = closeHour * 60 + closeMinute;
        
        const day = now.getDay();
        if (day < 1 || day > 5) return false;
        
        return currentTime >= openTime && currentTime < closeTime;
      };

      const calculateTimeToOpenFromLocal = (openTimeLocalStr: string | null, closeTimeLocalStr: string | null): string | null => {
        if (!openTimeLocalStr) return null;
        
        if (isMarketOpenLocal(openTimeLocalStr, closeTimeLocalStr)) {
          return null;
        }
        
        const now = new Date();
        const [openHour, openMinute] = openTimeLocalStr.split(':').map(Number);
        
        const todayOpen = new Date(now);
        todayOpen.setHours(openHour, openMinute, 0, 0);
        
        let nextOpen = todayOpen;
        if (now >= todayOpen) {
          nextOpen = new Date(todayOpen);
          nextOpen.setDate(nextOpen.getDate() + 1);
          
          while (nextOpen.getDay() === 0 || nextOpen.getDay() === 6) {
            nextOpen.setDate(nextOpen.getDate() + 1);
          }
        }
        
        const diff = nextOpen.getTime() - now.getTime();
        const hoursToOpen = Math.floor(diff / (1000 * 60 * 60));
        const minutesToOpen = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        return `${hoursToOpen}h ${minutesToOpen}m`;
      };

      const calculateTimeToCloseFromLocal = (closeTimeLocalStr: string | null): string | null => {
        if (!closeTimeLocalStr) return null;
        
        const now = new Date();
        const [closeHour, closeMinute] = closeTimeLocalStr.split(':').map(Number);
        
        const todayClose = new Date(now);
        todayClose.setHours(closeHour, closeMinute, 0, 0);
        
        let nextClose = todayClose;
        if (now >= todayClose) {
          nextClose = new Date(todayClose);
          nextClose.setDate(nextClose.getDate() + 1);
        }
        
        const diff = nextClose.getTime() - now.getTime();
        const hoursToClose = Math.floor(diff / (1000 * 60 * 60));
        const minutesToClose = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        return `${hoursToClose}h ${minutesToClose}m`;
      };

      if (holiday) {
        if (holiday.status === "closed") {
          const nextOpenUtc = getNextOpenUtc(marketLocalTime);
          const openTimeLocal = formatTimeLocal(nextOpenUtc);
          const closeTimeLocal = getCloseTimeLocal(marketLocalTime);
          const timeToOpenLocal = calculateTimeToOpenFromLocal(openTimeLocal, closeTimeLocal);
          return { isOpen: false, timeToOpen: timeToOpenLocal, timeToClose: null, openTimeLocal, closeTimeLocal };
        } else if (holiday.status === "early-close" && holiday.open && holiday.close) {
          const openTime = new Date(holiday.open).getTime();
          const closeTime = new Date(holiday.close).getTime();
          
          if (nowUtc >= openTime && nowUtc < closeTime) {
            const openTimeLocal = formatTimeLocal(openTime);
            const closeTimeLocal = formatTimeLocal(closeTime);
            const timeToCloseLocal = calculateTimeToCloseFromLocal(closeTimeLocal);
            return { isOpen: true, timeToOpen: null, timeToClose: timeToCloseLocal, openTimeLocal, closeTimeLocal };
          } else if (nowUtc < openTime) {
            const openTimeLocal = formatTimeLocal(openTime);
            const closeTimeLocal = formatTimeLocal(closeTime);
            const timeToOpenLocal = calculateTimeToOpenFromLocal(openTimeLocal, closeTimeLocal);
            return { isOpen: false, timeToOpen: timeToOpenLocal, timeToClose: null, openTimeLocal, closeTimeLocal };
          } else {
            const nextOpenUtc = getNextOpenUtc(marketLocalTime);
            const openTimeLocal = formatTimeLocal(nextOpenUtc);
            const closeTimeLocal = getCloseTimeLocal(marketLocalTime);
            const timeToOpenLocal = calculateTimeToOpenFromLocal(openTimeLocal, closeTimeLocal);
            return { isOpen: false, timeToOpen: timeToOpenLocal, timeToClose: null, openTimeLocal, closeTimeLocal };
          }
        }
      }
      
      const todayOpenUtc = getNextOpenUtc(new Date(marketLocalTime.getFullYear(), marketLocalTime.getMonth(), marketLocalTime.getDate()));
      const openTimeLocal = formatTimeLocal(todayOpenUtc);
      const closeTimeLocal = getCloseTimeLocal(marketLocalTime);
      const isOpenLocal = isMarketOpenLocal(openTimeLocal, closeTimeLocal);
      
      if (marketDay >= 1 && marketDay <= 5) {
        if (isOpenLocal) {
          const timeToCloseLocal = calculateTimeToCloseFromLocal(closeTimeLocal);
          return { isOpen: true, timeToOpen: null, timeToClose: timeToCloseLocal, openTimeLocal, closeTimeLocal };
        } else {
          const nextOpenUtc = getNextOpenUtc(marketLocalTime);
          const nextOpenTimeLocal = formatTimeLocal(nextOpenUtc);
          const timeToOpenLocal = calculateTimeToOpenFromLocal(nextOpenTimeLocal, closeTimeLocal);
          return { isOpen: false, timeToOpen: timeToOpenLocal, timeToClose: null, openTimeLocal, closeTimeLocal };
        }
      } else {
        const nextOpenUtc = getNextOpenUtc(marketLocalTime);
        const openTimeLocal = formatTimeLocal(nextOpenUtc);
        const closeTimeLocal = getCloseTimeLocal(marketLocalTime);
        const timeToOpenLocal = calculateTimeToOpenFromLocal(openTimeLocal, closeTimeLocal);
        return { isOpen: false, timeToOpen: timeToOpenLocal, timeToClose: null, openTimeLocal, closeTimeLocal };
      }
    };

    const updateMarketsStatus = () => {
      const markets: MarketInfo[] = [
        {
          ...calculateMarketStatus("NYSE", marketHours.NYSE.openHour, marketHours.NYSE.openMinute, marketHours.NYSE.closeHour, marketHours.NYSE.closeMinute, marketHours.NYSE.timezoneOffset),
          name: marketHours.NYSE.name,
          countryCode: marketHours.NYSE.countryCode
        },
        {
          ...calculateMarketStatus("NASDAQ", marketHours.NASDAQ.openHour, marketHours.NASDAQ.openMinute, marketHours.NASDAQ.closeHour, marketHours.NASDAQ.closeMinute, marketHours.NASDAQ.timezoneOffset),
          name: marketHours.NASDAQ.name,
          countryCode: marketHours.NASDAQ.countryCode
        },
        {
          ...calculateMarketStatus("LSE", marketHours.LSE.openHour, marketHours.LSE.openMinute, marketHours.LSE.closeHour, marketHours.LSE.closeMinute, marketHours.LSE.timezoneOffset),
          name: marketHours.LSE.name,
          countryCode: marketHours.LSE.countryCode
        },
        {
          ...calculateMarketStatus("TSE", marketHours.TSE.openHour, marketHours.TSE.openMinute, marketHours.TSE.closeHour, marketHours.TSE.closeMinute, marketHours.TSE.timezoneOffset),
          name: marketHours.TSE.name,
          countryCode: marketHours.TSE.countryCode
        },
        {
          ...calculateMarketStatus("BIST", marketHours.BIST.openHour, marketHours.BIST.openMinute, marketHours.BIST.closeHour, marketHours.BIST.closeMinute, marketHours.BIST.timezoneOffset),
          name: marketHours.BIST.name,
          countryCode: marketHours.BIST.countryCode
        },
        {
          ...calculateMarketStatus("XETR", marketHours.XETR.openHour, marketHours.XETR.openMinute, marketHours.XETR.closeHour, marketHours.XETR.closeMinute, marketHours.XETR.timezoneOffset),
          name: marketHours.XETR.name,
          countryCode: marketHours.XETR.countryCode
        }
      ];

      setMarketsStatus(markets);
    };

    updateMarketsStatus();
    const interval = setInterval(updateMarketsStatus, 60000);
    return () => clearInterval(interval);
  }, [currentUtcTime]);

  return (
    <div className="min-h-screen bg-app-box text-gray-900">
      {/* Header */}
      <header className="w-full py-3 md:py-4 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <img 
            src={logo} 
            alt="TraderHub24" 
            className="h-10 md:h-14 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => navigate('/')}
          />
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button
              onClick={() => navigate('/')}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:header.title')}
            </button>
            <a 
              href="/pricing"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/pricing';
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              Pricing
            </a>
            <a 
              href="/documentation"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/documentation';
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:footer.documentation')}
            </a>
            <button
              onClick={() => {
                setShowLogin(true);
              }}
              className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors"
            >
              {t('common:header.signIn')}
            </button>
            <button
              onClick={() => {
                setShowRegister(true);
              }}
              className="px-6 py-2.5 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
            >
              {t('pages:landing.getStarted')}
            </button>
          </nav>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {showMobileMenu ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {/* Mobile Menu */}
        {showMobileMenu && (
          <nav className="md:hidden mt-4 pb-4 border-b border-gray-200 animate-in slide-in-from-top duration-200">
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  navigate('/');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                {t('common:header.title')}
              </button>
              <a 
                href="/pricing"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  window.location.href = '/pricing';
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                Pricing
              </a>
              <a 
                href="/documentation"
                onClick={(e) => {
                  e.preventDefault();
                  setShowMobileMenu(false);
                  window.location.href = '/documentation';
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                {t('common:footer.documentation')}
              </a>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  setShowLogin(true);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-left"
              >
                {t('common:header.signIn')}
              </button>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  setShowRegister(true);
                }}
                className="px-4 py-2 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors text-sm mt-2"
              >
                {t('pages:landing.getStarted')}
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Page Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-4">
            {t('common:nav.tools')}
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Professional trading calculators and tools to help you manage risk, calculate position sizes, and analyze your trading performance.
          </p>
        </div>

        {/* Tools Section */}
        <section className="space-y-6">
          {/* Top Row: Win Rate and Profit Factor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Win Rate Calculator */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold">{t('tools:winRate.title')}</h2>
                <button
                  onClick={resetWinRateValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('tools:winRate.resetValues')}
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:winRate.winningTrades')}</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:winRate.winningTradesPlaceholder')}
                      value={winningTrades}
                      onChange={(e) => setWinningTrades(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:winRate.losingTrades')}</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:winRate.losingTradesPlaceholder')}
                      value={losingTrades}
                      onChange={(e) => setLosingTrades(e.target.value)}
                    />
                  </div>
                </div>
                {winRate !== null && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">{t('tools:winRate.winRate')}</p>
                      <p className="text-2xl md:text-3xl font-bold text-orange-700">{winRate.toFixed(2)}%</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('tools:winRate.winningOf', { winning: parseFloat(winningTrades) || 0, total: parseFloat(winningTrades) + parseFloat(losingTrades) || 0 })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Profit Factor Calculator */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold">{t('tools:profitFactor.title')}</h2>
                <button
                  onClick={resetProfitFactorValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('tools:profitFactor.resetValues')}
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:profitFactor.totalProfit')}</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:profitFactor.totalProfitPlaceholder')}
                      value={totalProfit}
                      onChange={(e) => setTotalProfit(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:profitFactor.totalLoss')}</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:profitFactor.totalLossPlaceholder')}
                      value={totalLoss}
                      onChange={(e) => setTotalLoss(e.target.value)}
                    />
                  </div>
                </div>
                {profitFactor !== null && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">{t('tools:profitFactor.profitFactor')}</p>
                      <p className="text-2xl md:text-3xl font-bold text-orange-700">{profitFactor.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('tools:profitFactor.calculation', { profit: parseFloat(totalProfit) || 0, loss: parseFloat(totalLoss) || 0 })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Row: Risk/Reward and Market Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* RR + Position Size */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold">{t('tools:riskReward.title')}</h2>
                <button
                  onClick={resetRiskRewardValues}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('tools:riskReward.resetValues')}
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.accountSize')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.accountSizePlaceholder')}
                      value={accountSize}
                      onChange={(e) => setAccountSize(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.riskPercent')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.riskPercentPlaceholder')}
                      value={riskPercent}
                      onChange={(e) => setRiskPercent(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.entry')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.entryPlaceholder')}
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.stop')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.stopPlaceholder')}
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.target1')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.target1Placeholder')}
                      value={target1}
                      onChange={(e) => setTarget1(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('tools:riskReward.target2')}</label>
                    <input
                      type="number"
                      step="any"
                      className="form-input w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder={t('tools:riskReward.target2Placeholder')}
                      value={target2}
                      onChange={(e) => setTarget2(e.target.value)}
                    />
                  </div>
                </div>
                {(rrResult.rr1 !== null || rrResult.rr2 !== null || rrResult.suggestedUnits !== null || rrResult.riskDollar !== null || rrResult.unitRiskDollar !== null) && (
                  <div className="mt-4 p-4 bg-orange-50 rounded-xl space-y-2">
                    {rrResult.rr1 !== null && (
                      <div className="flex justify-between">
                        <span className="font-medium text-sm md:text-base">{t('tools:riskReward.rr1')}</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr1.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.rr2 !== null && (
                      <div className="flex justify-between">
                        <span className="font-medium text-sm md:text-base">{t('tools:riskReward.rr2')}</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.rr2.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.suggestedUnits !== null && (
                      <div className="flex justify-between">
                        <span className="font-medium text-sm md:text-base">{t('tools:riskReward.suggestedUnits')}</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">{rrResult.suggestedUnits.toFixed(4)}</span>
                      </div>
                    )}
                    {rrResult.riskDollar !== null && (
                      <div className="flex justify-between">
                        <span className="font-medium text-sm md:text-base">{t('tools:riskReward.riskDollar')}</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.riskDollar.toFixed(2)}</span>
                      </div>
                    )}
                    {rrResult.unitRiskDollar !== null && (
                      <div className="flex justify-between">
                        <span className="font-medium text-sm md:text-base">{t('tools:riskReward.unitRiskDollar')}</span>
                        <span className="font-bold text-orange-700 text-sm md:text-base">${rrResult.unitRiskDollar.toFixed(4)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Market Hours Tools */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold">{t('tools:marketHours.title')}</h2>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{t('tools:marketHours.localTime')} </span>
                  <span className="font-mono">{localTime}</span>
                </div>
              </div>
              <div className="space-y-1">
                {marketsStatus.map((market, index) => (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center justify-between py-2 border-b border-gray-100 last:border-b-0 gap-1 sm:gap-0"
                  >
                    <div className="flex items-center gap-2">
                      <ReactCountryFlag
                        countryCode={market.countryCode}
                        svg
                        style={{
                          width: '1.2em',
                          height: '1.2em',
                        }}
                        title={market.countryCode}
                      />
                      <span className="text-sm font-medium text-gray-700">{market.name}</span>
                      {market.openTimeLocal && market.closeTimeLocal && (
                        <span className="text-[10px] md:text-xs text-gray-500 font-normal">
                          ({market.openTimeLocal} - {market.closeTimeLocal})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        market.isOpen 
                          ? "bg-green-100 text-green-700" 
                          : "bg-red-100 text-red-700"
                      }`}>
                        {market.isOpen ? t('common:status.open') : t('common:status.closed')}
                      </span>
                      {market.isOpen && market.timeToClose && (
                        <span className="text-[10px] md:text-xs text-gray-500">
                          {market.timeToClose}
                        </span>
                      )}
                      {!market.isOpen && market.timeToOpen && (
                        <span className="text-[10px] md:text-xs text-gray-500">
                          {market.timeToOpen}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mt-12 md:mt-16 bg-orange-50 border border-orange-200 rounded-2xl p-6 md:p-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Start Tracking Your Trades Today
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Sign up for free and get access to advanced trade tracking, analytics, strategies, and more. No credit card required.
          </p>
          <button
            onClick={() => setShowRegister(true)}
            className="px-8 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-md"
          >
            {t('pages:landing.getStarted')}
          </button>
        </section>
      </main>

      {/* Footer */}
      <Footer currentPage="tools" onNavigate={(page) => {
        if (page === "privacy-policy") navigate("/privacy");
        else if (page === "terms-and-conditions") navigate("/terms");
        else if (page === "risk-warning") navigate("/risk-warning");
        else if (page === "documentation") navigate("/documentation");
        else if (page === "contact") navigate("/contact");
        else if (page === "home") navigate('/');
        else if (page === "changelog") navigate('/changelog');
      }} />

      {/* Auth Modals */}
      {showLogin && (
        <Login
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
          onSuccess={() => navigate('/app')}
        />
      )}
      {showRegister && (
        <Register
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}
    </div>
  );
}

export default PublicTools;

